import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseModalRouteOptions {
  paramName?: string; // Default: 'modal'
  onOpen?: () => void;
  onClose?: () => void;
  preventFlash?: boolean; // Prevent modal flash on initial load
}

interface UseModalRouteReturn {
  isOpen: boolean;
  isMounted: boolean; // Use this for rendering to prevent flash
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Hook for managing modal state via URL parameters
 *
 * Features:
 * - Modal state persisted in URL
 * - Browser back button closes modal
 * - Shareable URLs that open specific modals
 * - Multiple simultaneous modals (different param names)
 * - No history pollution
 * - Prevents modal flash on page load
 *
 * @example
 * ```tsx
 * const SettingsModal = () => {
 *   const { isOpen, isMounted, open, close } = useModalRoute('settings');
 *
 *   if (!isOpen) return null;
 *
 *   return (
 *     <Modal isOpen={isMounted} onClose={close}>
 *       <h1>Settings</h1>
 *     </Modal>
 *   );
 * };
 * ```
 */
export const useModalRoute = (
  modalId: string,
  options: UseModalRouteOptions = {}
): UseModalRouteReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { paramName = 'modal', onOpen, onClose, preventFlash = true } = options;

  // Track previous open state to detect back button
  const prevIsOpenRef = useRef<boolean>(false);

  // Prevent flash on initial load
  const [isMounted, setIsMounted] = useState(false);

  // Check if this modal is currently open
  const isOpen = searchParams.get(paramName) === modalId;

  // Open modal by adding to URL
  const open = useCallback(() => {
    const currentModalId = searchParams.get(paramName);

    // Prevent history pollution - don't re-add if already open
    if (currentModalId === modalId) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set(paramName, modalId);
    setSearchParams(newParams, { replace: false }); // Create history entry
    onOpen?.();
  }, [searchParams, setSearchParams, modalId, paramName, onOpen]);

  // Close modal by removing from URL
  const close = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(paramName);
    setSearchParams(newParams, { replace: false });
    onClose?.();
  }, [searchParams, setSearchParams, paramName, onClose]);

  // Toggle modal state
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Handle browser back button
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;

    if (wasOpen && !isOpen) {
      // Modal was closed via back button
      onClose?.();
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, onClose]);

  // Prevent modal flash on page load
  useEffect(() => {
    if (isOpen && preventFlash) {
      // Small delay to prevent flash using requestAnimationFrame
      requestAnimationFrame(() => {
        setIsMounted(true);
      });
    } else {
      setIsMounted(false);
    }
  }, [isOpen, preventFlash]);

  return {
    isOpen,
    isMounted: preventFlash ? isMounted : isOpen,
    open,
    close,
    toggle
  };
};
